import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import ConversationList from './components/ConversationList';
import ChatArea from './components/ChatArea';
import RadioInfoPanel from './components/RadioInfoPanel';
import {
  liveChatConversations as mockConversations,
  type LiveChatConversation,
  type LiveChatMessage,
} from '@/mocks/superAdminLiveSupport';
import { type TranslationDirection } from '@/mocks/liveChatTranslator';
import { translateText } from '@/mocks/liveChatTranslator';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<LiveChatConversation[]> {
  return new Promise((resolve, reject) => {
    const delay = 700 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.93) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve(mockConversations);
    }, delay);
  });
}

function showAlert(icon: 'success' | 'error' | 'warning' | 'info', title: string, text?: string) {
  return Swal.fire({
    icon,
    title,
    text: text ?? '',
    confirmButtonColor: '#1a1a1a',
    customClass: {
      popup: 'rounded-2xl text-sm',
      title: 'text-sm font-bold',
      confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
    },
  });
}

const agentOptions = [
  { id: 'SA-001', name: 'Carlos Mendoza' },
  { id: 'SA-002', name: 'Ana López' },
  { id: 'SA-003', name: 'Andrés García' },
  { id: 'SA-004', name: 'Diana Ríos' },
];

const DEFAULT_EXTRA = {
  contractType: 'N/A',
  contractStatus: 'active' as const,
  contractEndDate: '',
  lastPaymentAmount: '$0',
  lastPaymentDate: '',
  lastPaymentMethod: '',
  ticketCount: 0,
  contactEmail: '',
  contactPhone: '',
};

export default function SuperAdminLiveSupport() {
  const [conversations, setConversations] = useState<LiveChatConversation[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeConvId, setActiveConvId] = useState<number | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [convSearch, setConvSearch] = useState('');

  /* ─── Load data ─── */
  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((d) => {
        const stored = localStorage.getItem('sa_live_transfers');
        const transfers: LiveChatConversation[] = stored ? JSON.parse(stored) : [];
        const allConversations = [...d];
        transfers.forEach((transfer) => {
          const exists = allConversations.some((c) => c.id === transfer.id);
          if (!exists) {
            allConversations.unshift({
              ...transfer,
              streamStatus: transfer.streamStatus || 'online',
              billingStatus: transfer.billingStatus || 'paid',
              billingAmount: transfer.billingAmount || '$29/mes',
              billingNextDate: transfer.billingNextDate || '',
              country: transfer.country || '',
              registeredAt: transfer.registeredAt || '',
              listeners: transfer.listeners || 0,
              messages: transfer.messages || [],
              ...DEFAULT_EXTRA,
            });
          }
        });
        setConversations(allConversations);
        setLoadingState('ready');
        if (allConversations.length > 0) setActiveConvId(allConversations[0].id);
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ─── Filtered conversations ─── */
  const filteredConversations = useMemo(() => {
    if (!convSearch.trim()) return conversations;
    const q = convSearch.toLowerCase();
    return conversations.filter(
      (c) =>
        c.radioName.toLowerCase().includes(q) ||
        c.frequency.toLowerCase().includes(q) ||
        c.planName.toLowerCase().includes(q) ||
        c.country.toLowerCase().includes(q),
    );
  }, [conversations, convSearch]);

  const activeConv = conversations.find((c) => c.id === activeConvId) ?? null;
  const totalUnread = conversations.reduce((s, c) => s + c.unread, 0);

  /* ─── Select conversation ─── */
  const selectConv = (id: number) => {
    setActiveConvId(id);
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
  };

  /* ─── Send message ─── */
  const handleSend = (content: string, translateMode: TranslationDirection | 'off') => {
    if (!activeConv) return;

    let contentToSend = content;
    let originalContent: string | undefined;
    let sourceLang: string | undefined;
    let targetLang: string | undefined;

    if (translateMode !== 'off') {
      const result = translateText(content, translateMode);
      originalContent = result.original;
      contentToSend = result.translated;
      const langLabels: Record<TranslationDirection, { source: string; target: string }> = {
        'es-to-en': { source: 'ES', target: 'EN' },
        'en-to-es': { source: 'EN', target: 'ES' },
      };
      sourceLang = langLabels[translateMode].source;
      targetLang = langLabels[translateMode].target;
    }

    const newMsg: LiveChatMessage = {
      id: Date.now(),
      from: 'sa',
      author: 'Carlos Mendoza',
      radioName: activeConv.radioName,
      content: contentToSend,
      originalContent,
      sourceLang,
      targetLang,
      timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
    };

    setConversations((prev) =>
      prev.map((c) =>
        c.id === activeConv.id
          ? { ...c, messages: [...c.messages, newMsg], lastMessage: contentToSend, lastMessageAt: newMsg.timestamp }
          : c,
      ),
    );

    /* auto-reply simulation */
    setTimeout(() => {
      const autoReply: LiveChatMessage = {
        id: Date.now() + 1,
        from: 'radio',
        author: activeConv.radioName,
        radioName: activeConv.radioName,
        content: '¡Gracias! Recibí tu mensaje. En breve te respondo.',
        timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
      };
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConv.id
            ? { ...c, messages: [...c.messages, autoReply], lastMessage: autoReply.content, lastMessageAt: autoReply.timestamp }
            : c,
        ),
      );
    }, 2000);
  };

  /* ─── Resolve ─── */
  const handleResolve = () => {
    if (!activeConv) return;
    Swal.fire({
      icon: 'question',
      title: '¿Marcar como resuelto?',
      text: `La conversación con ${activeConv.radioName} se marcará como resuelta.`,
      showCancelButton: true,
      confirmButtonText: 'Resolver',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((r) => {
      if (r.isConfirmed) {
        const resolvedMsg: LiveChatMessage = {
          id: Date.now(),
          from: 'sa',
          author: 'Carlos Mendoza',
          radioName: activeConv.radioName,
          content: 'Esta conversación fue marcada como resuelta.',
          timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
        };
        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeConv.id
              ? { ...c, messages: [...c.messages, resolvedMsg], lastMessage: 'Conversación resuelta', lastMessageAt: resolvedMsg.timestamp }
              : c,
          ),
        );
        showAlert('success', 'Conversación resuelta', `El chat con ${activeConv.radioName} fue marcado como resuelto.`);
      }
    });
  };

  /* ─── Transfer ─── */
  const handleTransfer = (agentId: string) => {
    const agent = agentOptions.find((a) => a.id === agentId);
    if (!activeConv || !agent) return;
    Swal.fire({
      icon: 'question',
      title: '¿Transferir conversación?',
      text: `Se transferirá a ${agent.name}.`,
      showCancelButton: true,
      confirmButtonText: 'Transferir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((r) => {
      if (r.isConfirmed) {
        const transferMsg: LiveChatMessage = {
          id: Date.now(),
          from: 'sa',
          author: 'Carlos Mendoza',
          radioName: activeConv.radioName,
          content: `Conversación transferida a ${agent.name}.`,
          timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
        };
        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeConv.id
              ? { ...c, messages: [...c.messages, transferMsg], lastMessage: `Transferido a ${agent.name}`, lastMessageAt: transferMsg.timestamp }
              : c,
          ),
        );
        showAlert('success', 'Conversación transferida', `El chat fue transferido a ${agent.name}.`);
      }
    });
  };

  /* ─── Create ticket ─── */
  const handleCreateTicket = () => {
    if (!activeConv) return;
    Swal.fire({
      title: 'Crear ticket desde chat',
      html: `
        <div style="text-align:left;font-size:12px;">
          <p style="margin-bottom:8px;color:#666;">Radio: <strong>${activeConv.radioName}</strong></p>
          <label style="display:block;margin-bottom:4px;color:#333;font-weight:600;font-size:11px;">Asunto del ticket</label>
          <input id="ticket-subject" class="swal2-input" style="width:100%;font-size:11px;padding:8px 12px;border-radius:10px;border:1px solid #e5e5e5;" placeholder="Ej: Problema con el stream">
          <label style="display:block;margin-top:10px;margin-bottom:4px;color:#333;font-weight:600;font-size:11px;">Descripción</label>
          <textarea id="ticket-desc" class="swal2-textarea" style="width:100%;font-size:11px;padding:8px 12px;border-radius:10px;border:1px solid #e5e5e5;resize:none;height:80px;" placeholder="Describí el problema..."></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Crear Ticket',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
      preConfirm: () => {
        const subject = (document.getElementById('ticket-subject') as HTMLInputElement)?.value;
        const desc = (document.getElementById('ticket-desc') as HTMLTextAreaElement)?.value;
        if (!subject?.trim()) {
          Swal.showValidationMessage('El asunto es requerido');
          return false;
        }
        return { subject, desc };
      },
    }).then((r) => {
      if (r.isConfirmed && r.value) {
        const ticketNumber = `TKT-${String(Math.floor(Math.random() * 9000) + 1000)}`;
        const ticketMsg: LiveChatMessage = {
          id: Date.now(),
          from: 'sa',
          author: 'Carlos Mendoza',
          radioName: activeConv.radioName,
          content: `Ticket ${ticketNumber} creado: "${r.value.subject}".`,
          timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
        };
        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeConv.id
              ? { ...c, messages: [...c.messages, ticketMsg], lastMessage: `Ticket ${ticketNumber} creado`, lastMessageAt: ticketMsg.timestamp }
              : c,
          ),
        );
        showAlert('success', 'Ticket creado', `Se creó el ticket ${ticketNumber} desde el chat con ${activeConv.radioName}.`);
      }
    });
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1800px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Dashboard
              </Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Live Support Chat</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Live Support Chat</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${conversations.length} conversaciones · ${totalUnread} sin leer`
                    : 'Cargando...'}
                </p>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 self-start sm:self-auto">
                <i className="ri-information-line text-[9px]"></i>
                Modo simulación
              </span>
            </div>

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="space-y-3 w-full max-w-md mx-auto">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 animate-pulse">
                      <div className="w-10 h-10 rounded-full bg-background-200"></div>
                      <div className="flex-1 space-y-1">
                        <div className="w-32 h-3 bg-background-200 rounded"></div>
                        <div className="w-48 h-2 bg-background-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* ─── 3-Panel Gmail Layout ─── */}
            {loadingState === 'ready' && (
              <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-280px)] min-h-[500px]">
                {/* Left: Conversations */}
                <ConversationList
                  conversations={filteredConversations}
                  activeId={activeConvId}
                  totalUnread={totalUnread}
                  search={convSearch}
                  onSearchChange={setConvSearch}
                  onSelect={selectConv}
                />

                {/* Center: Chat */}
                <ChatArea
                  conversation={activeConv}
                  chatInput={chatInput}
                  onChatInputChange={setChatInput}
                  onSend={handleSend}
                  onResolve={handleResolve}
                  onTransfer={handleTransfer}
                  onCreateTicket={handleCreateTicket}
                />

                {/* Right: Radio Info */}
                <RadioInfoPanel conversation={activeConv} />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Live Support</p>
      </div>
    </div>
  );
}